import type { EmailDisplay } from "./email-display.ts";
import type { DraftFreshness } from "./inbox-view.ts";
import { draftAllowsForDelay, type LateReplyView, replyAge } from "./late-reply-view.ts";

/*
 * What the reply step shows above its draft (docs/2026-10-05-review-reply-header.md): the message
 * being answered, in full, instead of a row of links to it. Pure, so the page and the tests share
 * it. Everything here reads the thread the page already holds; nothing is fetched.
 */

type ThreadMessage = {
  id: string;
  from: string;
  at: string;
  direction: "inbound" | "outbound";
  plainText: string;
};

export type RepliedMessageView<M = ThreadMessage> =
  /** No thread yet, or nothing in it the lead wrote. */
  | { kind: "none" }
  /** The draft's own record names this message: "Replying to". */
  | { kind: "answers"; message: M; newer: M | null }
  /**
   * The record of which message the draft answers has not loaded, or the draft predates that
   * record. The lead's newest message is shown and nothing is claimed about the draft.
   */
  | { kind: "latest"; message: M };

export function repliedMessageView<M extends ThreadMessage>(
  freshness: DraftFreshness,
  messages: readonly M[],
): RepliedMessageView<M> {
  if (freshness.kind === "unknown") {
    const latest = messages.findLast((message) => message.direction === "inbound");
    return latest ? { kind: "latest", message: latest } : { kind: "none" };
  }
  const message = messages.find((candidate) => candidate.id === freshness.answers.id);
  if (!message) return { kind: "none" };
  const newer =
    freshness.kind === "stale"
      ? (messages.find((candidate) => candidate.id === freshness.newer.id) ?? null)
      : null;
  return { kind: "answers", message, newer };
}

const BLOCKS = new Set([
  "P",
  "DIV",
  "LI",
  "TR",
  "UL",
  "OL",
  "TABLE",
  "BLOCKQUOTE",
  "PRE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HR",
]);
const SKIPPED = new Set(["STYLE", "SCRIPT", "HEAD", "TITLE"]);
const ELEMENT = 1;
const TEXT = 3;

function collect(node: Node, out: string[]): void {
  if (node.nodeType === TEXT) {
    out.push((node.textContent ?? "").replaceAll(/\s+/g, " "));
    return;
  }
  if (node.nodeType !== ELEMENT) return;
  const name = node.nodeName.toUpperCase();
  if (SKIPPED.has(name)) return;
  if (name === "BR") {
    out.push("\n");
    return;
  }
  const block = BLOCKS.has(name);
  if (block) out.push("\n\n");
  for (const child of node.childNodes) collect(child, out);
  if (block) out.push("\n\n");
}

const tidy = (text: string): string =>
  text
    .replaceAll(" ", " ")
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replaceAll(/\n{3,}/g, "\n\n")
    .trim();

/**
 * The words the sender wrote in this message, as paragraphs of plain text. The quoted history is
 * left out exactly where the thread's reading frame folds it (`quoted-history.ts`), so the two
 * never disagree about where a message ends. A message with nothing left falls back to its stored
 * plain text rather than showing an empty block.
 */
export function writtenText(
  display: EmailDisplay | undefined,
  plainText: string,
  parse: (html: string) => Document,
): string {
  if (!display) return tidy(plainText);
  const body = parse(display.html).body;
  if (display.fold.kind === "folded") {
    for (const quoted of [...body.querySelectorAll("[data-lcr-quote]")]) quoted.remove();
  }
  const out: string[] = [];
  collect(body, out);
  return tidy(out.join("")) || tidy(plainText);
}

/** Paragraphs, each a list of lines: what the block renders. */
export const paragraphsOf = (text: string): string[][] =>
  text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.split("\n").filter((line) => line !== ""))
    .filter((lines) => lines.length > 0);

/**
 * Past this the block opens folded. A cold-outbound reply is usually a few lines, so the common
 * case shows whole; a long one keeps the draft on screen until the rep asks for the rest.
 */
const REPLIED_FOLD = { lines: 8, characters: 560 } as const;

export function foldsWhenLong(text: string): boolean {
  if (text.length > REPLIED_FOLD.characters) return true;
  return paragraphsOf(text).reduce((sum, lines) => sum + lines.length, 0) > REPLIED_FOLD.lines;
}

/**
 * The other messages a step drew on, each named by who wrote it and when: never one noun repeated.
 * A message the thread does not hold is left out, since its link would land on nothing, and so is
 * the one already shown in full.
 */
export function sourceMessages<M extends { id: string }>(
  sources: readonly { messageId: string }[],
  messages: readonly M[],
  shownId: string | null,
): M[] {
  const seen = new Set<string>(shownId === null ? [] : [shownId]);
  const found: M[] = [];
  for (const source of sources) {
    if (seen.has(source.messageId)) continue;
    seen.add(source.messageId);
    const message = messages.find((candidate) => candidate.id === source.messageId);
    if (message) found.push(message);
  }
  return found;
}

/**
 * What a held reply changes for the rep: the lead has been waiting, and, where the draft was written
 * past the workspace's threshold, that it allows for the delay (`draftAllowsForDelay`). Said only
 * from the second day; "today" is no delay and earns no line.
 */
export function lateReplyNote(view: LateReplyView, now: Date): string | null {
  const age = replyAge(view.receivedAt, now);
  if (age === "today") return null;
  const waited = age === "yesterday" ? "since yesterday" : age.replace(" ago", "");
  return draftAllowsForDelay(view)
    ? `The lead has waited ${waited}. The draft allows for the delay.`
    : `The lead has waited ${waited}.`;
}
